import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { topics, siteMeta } from '@/mocks/site';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/topics', label: '爆料库' },
  { to: '/about', label: '关于我们' },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMobileOpen(false);
    setTopicOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setTopicOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/topics?q=${encodeURIComponent(value)}` : '/topics');
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="看瓜爆料 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-base font-bold text-background-50">
            瓜
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-brand text-lg text-foreground-950">{siteMeta.name}</span>
            <span className="hidden text-[10px] tracking-wide text-foreground-500 sm:block">
              独家 · 一手 · 内部首发
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navLinks.map((link) => {
            const active =
              link.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-primary-100 font-medium text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setTopicOpen((prev) => !prev)}
              className={`flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm transition-colors ${
                topicOpen
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
              aria-expanded={topicOpen}
            >
              主题分类
              <i
                className={`ri-arrow-down-s-line text-base transition-transform ${
                  topicOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>
            {topicOpen && (
              <div className="absolute left-0 top-full z-50 mt-2 w-[560px] rounded-lg border border-background-200/70 bg-background-50 p-4 shadow-none">
                <p className="mb-3 text-xs text-foreground-500">按主题追更，选择你感兴趣的爆料方向</p>
                <div className="grid grid-cols-2 gap-2">
                  {topics.map((topic) => (
                    <Link
                      key={topic.slug}
                      to={`/topic/${topic.slug}`}
                      className="flex items-start gap-3 rounded-md border border-transparent p-2.5 transition-colors hover:border-primary-200 hover:bg-primary-50"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i className={`${topic.icon} text-lg`}></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-950">
                          {topic.name}
                        </span>
                        <span className="line-clamp-1 block text-xs text-foreground-500">
                          {topic.desc}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden items-center md:flex">
            <div className="flex items-center rounded-full border border-background-200/70 bg-background-100/70 px-3 py-1.5 transition-colors focus-within:border-primary-300">
              <i className="ri-search-line mr-2 text-sm text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索爆料关键词"
                className="w-32 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400 lg:w-40"
                aria-label="搜索爆料"
              />
            </div>
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200/70 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </button>

          <Link
            to="/#subscribe"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            订阅爆料
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200/70 text-foreground-700 transition-colors hover:border-primary-300 lg:hidden"
            aria-label="打开菜单"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 py-3 md:hidden">
          <form onSubmit={handleSearch} className="flex items-center rounded-full border border-background-200/70 bg-background-100/70 px-3 py-2">
            <i className="ri-search-line mr-2 text-sm text-foreground-500"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索爆料关键词"
              className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              aria-label="搜索爆料"
            />
            <button type="submit" className="cursor-pointer whitespace-nowrap text-sm text-primary-600">
              搜索
            </button>
          </form>
        </div>
      )}

      {mobileOpen && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <p className="mb-2 mt-4 px-3 text-xs text-foreground-500">主题分类</p>
          <div className="grid grid-cols-2 gap-2">
            {topics.map((topic) => (
              <Link
                key={topic.slug}
                to={`/topic/${topic.slug}`}
                className="flex items-center gap-2 rounded-md border border-background-200/70 bg-background-100/60 px-3 py-2 text-sm text-foreground-800"
              >
                <i className={`${topic.icon} text-base text-accent-600`}></i>
                {topic.name}
              </Link>
            ))}
          </div>
          <Link
            to="/#subscribe"
            className="mt-4 block whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-center text-sm font-medium text-background-50"
          >
            订阅爆料
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;